import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Download, Eye, FileText, History, Loader2, Upload } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ToastHelper from "@/utils/toastHelper";
import { STUDENT_DOCUMENT_ACCEPT, STUDENT_DOCUMENT_MAX_BYTES, STUDENT_DOCUMENT_TYPES } from "@/constants/studentDocuments";
import { studentDocumentsService } from "@/services/studentDocumentsService";
import CourseProgressStageLayout from "@/components/course-progress/CourseProgressStageLayout";
import { getCourseProgressUrl } from "@/components/course-progress/courseProgressNavigation";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PHOTO_TYPES = [
  [STUDENT_DOCUMENT_TYPES.DRIVER_LICENCE, "Australian Driver Licence"],
  [STUDENT_DOCUMENT_TYPES.PASSPORT, "Australian Passport"],
  [STUDENT_DOCUMENT_TYPES.OTHER_ACCEPTABLE_PHOTO_ID, "Other Acceptable Photo ID"],
];

const RESIDENCY_TYPES = [
  [STUDENT_DOCUMENT_TYPES.MEDICARE_CARD, "Medicare Card"],
  [STUDENT_DOCUMENT_TYPES.CENTRELINK_HEALTH_CARE_CARD, "Centrelink Health Care Card"],
  [STUDENT_DOCUMENT_TYPES.PASSPORT, "Australian Passport"],
  [STUDENT_DOCUMENT_TYPES.NEW_ZEALAND_PASSPORT, "New Zealand Passport"],
  [STUDENT_DOCUMENT_TYPES.AUSTRALIAN_BIRTH_CERTIFICATE, "Australian Birth Certificate"],
  [STUDENT_DOCUMENT_TYPES.AUSTRALIAN_CITIZENSHIP_CERTIFICATE, "Australian Citizenship Certificate"],
  [STUDENT_DOCUMENT_TYPES.VEVO_CHECK, "VEVO Check"],
];

const getErrorMessage = (error) => error?.response?.data?.message || error?.message || "Unable to upload this document.";

const openBlob = (response, document, download) => {
  const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data]);
  const objectUrl = URL.createObjectURL(blob);
  if (download) {
    const link = window.document.createElement("a");
    link.href = objectUrl;
    link.download = getDocumentValue(document, "fileName") || "document";
    link.click();
  } else {
    window.open(objectUrl, "_blank", "noopener,noreferrer");
  }
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

export default function EnrollmentDocumentsStage({ mode = "student" }) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const studentBookingId = Number(params.studentBookingId || params.id || state?.studentBookingId || 0);
  const studentEnrollmentId = Number(state?.studentEnrollmentId || 0);
  const courseUrl = state?.courseProgressUrl || getCourseProgressUrl(mode, studentBookingId);
  const isAdmin = mode === "admin";
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [actioning, setActioning] = useState(null);
  const [message, setMessage] = useState("");
  const [showWorkflowHistory, setShowWorkflowHistory] = useState(false);
  const [localSelections, setLocalSelections] = useState({});
  const [returnDialog, setReturnDialog] = useState(null);

  const loadDocuments = useCallback(async () => {
    if (isAdmin && !studentEnrollmentId) {
      setLoading(false);
      setMessage("The enrolment must be created before identity documents can be reviewed.");
      return;
    }
    try {
      setMessage("");
      setDocuments(await studentDocumentsService.getEnrollmentDocuments(studentEnrollmentId));
    } catch (error) {
      setMessage(error?.response?.data?.message || "Unable to load identity documents.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, studentEnrollmentId]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const uploadedByType = useMemo(() => new Map(
    (Array.isArray(documents) ? documents : []).map((document) => [Number(getDocumentValue(document, "studentDocTypeId")), document]),
  ), [documents]);
  const activeStudentEnrollmentId = useMemo(() => {
    if (studentEnrollmentId > 0) return studentEnrollmentId;
    const documentWithEnrollment = (Array.isArray(documents) ? documents : []).find((document) => Number(getDocumentValue(document, "studentEnrollmentId") || 0) > 0);
    return Number(getDocumentValue(documentWithEnrollment, "studentEnrollmentId") || 0);
  }, [documents, studentEnrollmentId]);
  const documentWorkflowItems = useMemo(() => ([
    getRequirementWorkflow("Question 1", "Photo ID document", PHOTO_TYPES, uploadedByType, localSelections.photo),
    getRequirementWorkflow("Question 2", "Residency / visa proof", RESIDENCY_TYPES, uploadedByType, localSelections.residency),
  ]), [uploadedByType, localSelections]);
  const pageWorkflow = useMemo(() => getPageWorkflow(documentWorkflowItems), [documentWorkflowItems]);

  const selectDocument = (slot, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const selectedOptions = slot === "photo" ? PHOTO_TYPES : RESIDENCY_TYPES;
    const existingDocument = selectedOptions.map(([id]) => uploadedByType.get(id)).find(Boolean);
    if (existingDocument && existingDocument.canUploadDocuments !== true) {
      setMessage("This document is locked while waiting for admin review.");
      return;
    }
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!["jpg", "jpeg", "png", "pdf"].includes(extension)) return setMessage("Only JPG, JPEG, PNG and PDF files are allowed.");
    if (file.size > STUDENT_DOCUMENT_MAX_BYTES) return setMessage("The selected file exceeds the 10 MB size limit.");

    const typeId = Number(event.target.form?.elements?.[`${slot}Type`]?.value);
    setMessage("");
    setLocalSelections((current) => ({ ...current, [slot]: { file, studentDocTypeId: typeId } }));
  };

  const submitDocuments = async () => {
    const photoUploaded = optionsHaveUsableDocument(PHOTO_TYPES, uploadedByType, localSelections.photo);
    const residencyUploaded = optionsHaveUsableDocument(RESIDENCY_TYPES, uploadedByType, localSelections.residency);
    if (!photoUploaded || !residencyUploaded) {
      setMessage("Please upload all required documents before submitting.");
      return;
    }

    const documentsToSubmit = Object.entries(localSelections)
      .filter(([, selection]) => selection?.file)
      .map(([slot, selection]) => ({ slot, ...selection }));
    if (documentsToSubmit.length === 0) {
      setMessage("Please choose at least one document before submitting.");
      return;
    }
    if (!activeStudentEnrollmentId) {
      setMessage("The enrolment must be created before identity documents can be submitted.");
      return;
    }

    setUploading("submit");
    try {
      await studentDocumentsService.submitEnrollmentDocuments({ studentEnrollmentId: activeStudentEnrollmentId, documents: documentsToSubmit });
      ToastHelper.success("Documents submitted for review.");
      setLocalSelections({});
      navigate(courseUrl, { state: { focusCurrentStep: true } });
    } catch (error) {
      setMessage(getErrorMessage(error));
    } finally {
      setUploading(null);
    }
  };

  const viewDocument = async (document) => {
    try {
      openBlob(await studentDocumentsService.viewEnrollmentDocument(getDocumentValue(document, "studentDocId"), activeStudentEnrollmentId), document, false);
    } catch (error) {
      ToastHelper.apiError(error, "Unable to view this document.");
    }
  };

  const downloadDocument = async (document) => {
    try {
      openBlob(await studentDocumentsService.downloadEnrollmentDocument(getDocumentValue(document, "studentDocId"), activeStudentEnrollmentId), document, true);
    } catch (error) {
      ToastHelper.apiError(error, "Unable to download this document.");
    }
  };

  const runWorkflowAction = async (document, button) => {
    const studentDocId = Number(getDocumentValue(document, "studentDocId"));
    const nextStatus = Number(button.nextWorkFlowStatusNo ?? button.NextWorkFlowStatusNo);
    const text = button.text ?? button.Text ?? "Update";
    if (text.toLowerCase().includes("return")) {
      setReturnDialog({
        document,
        button,
        remarks: "",
      });
      return;
    }

    setActioning(`${studentDocId}-${nextStatus}`);
    try {
      await studentDocumentsService.updateEnrollmentDocumentWorkflow({
        studentDocId,
        studentEnrollmentId: activeStudentEnrollmentId,
        nextWorkFlowStatusNo: nextStatus,
        remarks: "",
      });
      ToastHelper.success("Document status saved.");
      navigate(courseUrl, { state: { focusCurrentStep: true } });
    } catch (error) {
      ToastHelper.apiError(error, "Unable to save document status.");
    } finally {
      setActioning(null);
    }
  };

  const confirmReturnWorkflowAction = async () => {
    const document = returnDialog?.document;
    const button = returnDialog?.button;
    const remarks = returnDialog?.remarks || "";
    const studentDocId = Number(getDocumentValue(document, "studentDocId"));
    const nextStatus = Number(button?.nextWorkFlowStatusNo ?? button?.NextWorkFlowStatusNo);

    if (!remarks.trim()) {
      ToastHelper.error("Return reason is required.");
      return;
    }

    setActioning(`${studentDocId}-${nextStatus}`);
    try {
      await studentDocumentsService.updateEnrollmentDocumentWorkflow({
        studentDocId,
        studentEnrollmentId: activeStudentEnrollmentId,
        nextWorkFlowStatusNo: nextStatus,
        remarks,
      });
      ToastHelper.success("Document status saved.");
      setReturnDialog(null);
      navigate(courseUrl, { state: { focusCurrentStep: true } });
    } catch (error) {
      ToastHelper.apiError(error, "Unable to save document status.");
    } finally {
      setActioning(null);
    }
  };

  return (
    <CourseProgressStageLayout backUrl={courseUrl} className="mx-auto max-w-5xl space-y-5" testId="identity-documents">
      <div className="wc-card overflow-hidden p-0">
        <div className="bg-[#0A78BE] px-5 py-3 text-center text-sm font-bold text-white">Identity / Documents</div>
        <div className="border-b-4 border-green-500 bg-slate-50 px-5 py-2 text-center text-xs font-semibold text-slate-600">
          Document workflow review
        </div>
        <div className="space-y-5 p-5 sm:p-7">
          <div className="flex flex-col gap-3 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
            <p className="text-sm text-slate-600">
              {isAdmin ? "Review each uploaded identity document, approve it, return it for changes, and view its workflow history." : "Upload one document for each requirement. Uploaded documents are reviewed by the training team."}
            </p>
            {activeStudentEnrollmentId > 0 && (
              <button type="button" className="wc-btn-secondary h-11 min-w-[210px] shrink-0 justify-center gap-2 whitespace-nowrap px-4 text-xs" onClick={() => setShowWorkflowHistory((value) => !value)}>
                <History className="h-4 w-4" />
                {showWorkflowHistory ? "Hide workflow" : "View document workflow"}
              </button>
            )}
          </div>
          {showWorkflowHistory && activeStudentEnrollmentId > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <h2 className="mb-4 font-display text-lg font-extrabold text-[#0A2540]">Document workflow</h2>
              <DocumentWorkflowTimeline items={documentWorkflowItems} />
            </div>
          )}
          {message && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" role="alert">{message}</div>}
          {loading ? (
            <div className="flex justify-center py-10 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <>
              {!isAdmin && <DocumentPageSummary workflow={pageWorkflow} />}
              <RequirementCard title="Question 1" heading="Photo ID document" options={PHOTO_TYPES} documents={uploadedByType} slot="photo" selection={localSelections.photo} uploading={uploading} actioning={actioning} isAdmin={isAdmin} onSelectDocument={selectDocument} onView={viewDocument} onDownload={downloadDocument} onWorkflowAction={runWorkflowAction} />
              <RequirementCard title="Question 2" heading="Residency / visa proof" options={RESIDENCY_TYPES} documents={uploadedByType} slot="residency" selection={localSelections.residency} uploading={uploading} actioning={actioning} isAdmin={isAdmin} onSelectDocument={selectDocument} onView={viewDocument} onDownload={downloadDocument} onWorkflowAction={runWorkflowAction} />
              {!isAdmin && pageWorkflow.canSubmit && (
                <div className="flex justify-center border-t border-slate-200 pt-5">
                  <button type="button" className="wc-btn-primary min-w-[190px]" disabled={uploading === "submit" || Object.keys(localSelections).length === 0} onClick={submitDocuments}>
                    {uploading === "submit" && <Loader2 className="h-4 w-4 animate-spin" />}
                    {pageWorkflow.submitText}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <ReturnReasonDialog
        value={returnDialog}
        onChange={(remarks) => setReturnDialog((current) => (current ? { ...current, remarks } : current))}
        onClose={() => setReturnDialog(null)}
        onConfirm={confirmReturnWorkflowAction}
        processing={Boolean(returnDialog && actioning === `${Number(getDocumentValue(returnDialog.document, "studentDocId"))}-${Number(returnDialog.button?.nextWorkFlowStatusNo ?? returnDialog.button?.NextWorkFlowStatusNo)}`)}
      />
    </CourseProgressStageLayout>
  );
}

function RequirementCard({ title, heading, options, documents, slot, selection, uploading, actioning, isAdmin, onSelectDocument, onView, onDownload, onWorkflowAction }) {
  const uploaded = options.map(([id]) => documents.get(id)).find(Boolean);
  const studentDocId = getDocumentValue(uploaded, "studentDocId");
  const buttons = getWorkflowButtons(uploaded);
  const workflowStatusName = getDocumentValue(uploaded, "workFlowStatusName") || "Required";
  const statusName = String(workflowStatusName).toLowerCase();
  const canUpload = !isAdmin && (!uploaded || getDocumentValue(uploaded, "canUploadDocuments") === true);
  const workflowRemarks = getDocumentValue(uploaded, "workflowRemarks");
  const returnedReason = workflowRemarks && statusName.includes("returned");
  const defaultDocumentTypeId = selection?.studentDocTypeId || getDocumentValue(uploaded, "studentDocTypeId") || options[0][0];
  const documentMessage = getDocumentMessage(workflowStatusName);
  const lockedMessage = statusName.includes("approved")
    ? "This document has been approved."
    : "This document is locked while it is waiting for admin review.";

  return (
    <section className="rounded-lg border border-green-200 bg-green-50 p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-display text-lg font-extrabold text-green-800">{title}</h2>
          <h3 className="mt-1 text-sm font-bold text-green-700">{heading}</h3>
        </div>
        <StatusBadge status={workflowStatusName} />
      </div>

      <p className="mt-3 text-sm text-green-800">It must be ONE of the following:</p>
      <ul className="mt-2 grid gap-1 pl-5 text-sm text-green-800 sm:grid-cols-2">
        {options.map(([, label]) => (
          <li className="list-disc" key={label}>
            {label === "Other Acceptable Photo ID" ? (
              <a href="/assets/studentdocument/Other%20Acceptable%20Photo%20ID.pdf" target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700 hover:underline">
                {label}
              </a>
            ) : label}
          </li>
        ))}
      </ul>
      <RequirementGuidance slot={slot} />

      <div className="mt-5 rounded-lg bg-white p-3 text-sm text-slate-700 ring-1 ring-green-100">
        {selection?.file ? (
          <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-blue-600" /><span className="truncate">{selection.file.name}</span><span className="ml-auto rounded-full bg-blue-50 px-2 py-0.5 text-xs font-bold text-blue-700">In Progress</span></div>
        ) : uploaded ? (
          <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /><span className="truncate">{getDocumentValue(uploaded, "fileName")}</span></div>
        ) : (
          "No document uploaded yet."
        )}
      </div>

      {returnedReason && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <span className="font-bold">Return reason: </span>{workflowRemarks}
        </div>
      )}
      {documentMessage && !returnedReason && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-600">{documentMessage}</div>
      )}

      <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        {canUpload ? (
          <form className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <select name={`${slot}Type`} defaultValue={defaultDocumentTypeId} className="wc-input min-w-[240px] text-sm">{options.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select>
            <label className="wc-btn-primary h-11 min-w-[150px] cursor-pointer justify-center gap-2 whitespace-nowrap px-4 text-xs">
              <Upload className="h-3.5 w-3.5" />
              {uploading === slot ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : uploaded ? "Replace document" : "Choose file"}
              <input type="file" className="hidden" accept={STUDENT_DOCUMENT_ACCEPT} disabled={Boolean(uploading)} onChange={(event) => onSelectDocument(slot, event)} />
            </label>
          </form>
        ) : <span className="text-sm text-slate-500">{isAdmin ? "File actions are separate from workflow actions." : lockedMessage}</span>}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {studentDocId && (
            <>
              <button type="button" className="wc-btn-secondary h-11 min-w-[104px] justify-center gap-2 whitespace-nowrap px-4 text-xs" onClick={() => onView(uploaded)}><Eye className="h-4 w-4" /> View</button>
              <button type="button" className="wc-btn-secondary h-11 min-w-[122px] justify-center gap-2 whitespace-nowrap px-4 text-xs" onClick={() => onDownload(uploaded)}><Download className="h-4 w-4" /> Download</button>
            </>
          )}
          {isAdmin && buttons.map((button) => {
            const nextStatus = Number(button.nextWorkFlowStatusNo ?? button.NextWorkFlowStatusNo);
            const text = button.text ?? button.Text ?? "Update";
            const busy = actioning === `${studentDocId}-${nextStatus}`;
            return (
              <button key={`${studentDocId}-${nextStatus}`} type="button" className={getActionClass(text)} onClick={() => onWorkflowAction(uploaded, button)} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                {text}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function RequirementGuidance({ slot }) {
  if (slot === "photo") {
    return (
      <div className="mt-4 space-y-3 text-sm text-green-800">
        <p className="font-semibold">
          Please make sure all information in your uploaded document is clearly identifiable. Only coloured copy is acceptable.
        </p>
        <p className="font-semibold">
          Please also make sure you have the original copy available with you during the class. According to the WorkSafe VIC&apos;s requirement, your Photo ID must be verified by your trainer at the beginning of your class.
        </p>
      </div>
    );
  }

  if (slot === "residency") {
    return (
      <div className="mt-4 space-y-4 text-sm text-green-800">
        <p className="font-semibold italic text-rose-600">
          Please read the required document list carefully. Uploading the wrong document will delay the enrolment process.
        </p>
        <div className="space-y-2">
          <p className="font-semibold">Australian Citizen, Permanent Resident or New Zealand Citizen:</p>
          <p className="font-semibold">
            Lost your card?{" "}
            <a href="https://www.servicesaustralia.gov.au/medicare-card" target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700 hover:underline">
              Learn how to get your digital Medicare Card.
            </a>
          </p>
        </div>
        <div className="space-y-2">
          <p className="font-semibold">International Student and Overseas Visa Holders, it must be the VEVO Check (original PDF issued within 2 days of submission).</p>
          <p className="font-semibold">
            <a href="https://immi.homeaffairs.gov.au/visas/already-have-a-visa/check-visa-details-and-conditions/check-conditions-online" target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700 hover:underline">
              Learn how to get your Vevo Check online.
            </a>
          </p>
          <p className="font-semibold italic text-rose-600">
            Please note that the Visa Grant Letter is NOT Vevo Check and we do NOT accept any kind of screenshot of visa document.
          </p>
        </div>
        <p className="font-semibold">
          Please make sure all information in your uploaded document is clearly identifiable.
        </p>
      </div>
    );
  }

  return null;
}

function getRequirementWorkflow(title, heading, options, uploadedByType, selection) {
  const uploaded = options.map(([id]) => uploadedByType.get(id)).find(Boolean);
  if (selection?.file) {
    return {
      title,
      heading,
      status: "Ready to Submit",
      fileName: selection.file.name,
      updatedDate: null,
      updatedBy: "Student",
      remarks: "",
    };
  }

  return {
    title,
    heading,
    status: getDocumentValue(uploaded, "workFlowStatusName") || "Required",
    fileName: getDocumentValue(uploaded, "fileName") || "",
    updatedDate: getDocumentValue(uploaded, "workflowUpdatedDate") || getDocumentValue(uploaded, "uploadedDate") || null,
    updatedBy: getDocumentValue(uploaded, "workflowUpdatedBy") || "",
    remarks: getDocumentValue(uploaded, "workflowRemarks") || "",
  };
}

function DocumentPageSummary({ workflow }) {
  return (
    <section className={`rounded-lg border p-4 ${workflow.className}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-xs font-extrabold uppercase tracking-[0.18em] text-slate-500">Current status</div>
          <h2 className="mt-1 font-display text-xl font-extrabold text-[#0A2540]">{workflow.title}</h2>
          <p className="mt-2 text-sm text-slate-700">{workflow.message}</p>
        </div>
        <StatusBadge status={workflow.status} />
      </div>
      <div className="mt-4 rounded-lg bg-white/75 p-3 text-sm text-slate-700 ring-1 ring-white/80">
        <span className="font-bold text-[#0A2540]">Next action: </span>{workflow.nextAction}
      </div>
    </section>
  );
}

function getPageWorkflow(items) {
  const statuses = items.map((item) => normalizeWorkflowStatus(item.status));
  const hasSelection = statuses.some((status) => status === "ready");
  const hasReturned = statuses.some((status) => status === "returned");
  const hasPending = statuses.some((status) => status === "pending");
  const allApproved = statuses.length > 0 && statuses.every((status) => status === "approved");
  const allSubmitted = statuses.length > 0 && statuses.every((status) => ["approved", "pending"].includes(status));

  if (hasReturned) {
    return {
      status: "Action Required",
      title: "One or more documents were returned",
      message: "Please review the reason, replace the required document and resubmit.",
      nextAction: "Replace the returned document and resubmit your documents.",
      submitText: "Resubmit Documents",
      canSubmit: true,
      className: "border-amber-200 bg-amber-50",
    };
  }

  if (allSubmitted && hasPending) {
    return {
      status: "Pending Review",
      title: "Documents submitted",
      message: "Your documents have been submitted and are waiting for admin review. No action is required from you at this time.",
      nextAction: "Your documents are awaiting admin review. You can continue to the next course step.",
      submitText: "",
      canSubmit: false,
      className: "border-blue-200 bg-blue-50",
    };
  }

  if (allApproved) {
    return {
      status: "Completed",
      title: "Documents approved",
      message: "All required identity and supporting documents have been approved.",
      nextAction: "No further document action is required.",
      submitText: "",
      canSubmit: false,
      className: "border-emerald-200 bg-emerald-50",
    };
  }

  if (hasSelection) {
    return {
      status: "In Progress",
      title: "Documents ready to submit",
      message: "One or more documents are selected and ready to submit for review.",
      nextAction: "Submit the selected documents for admin review.",
      submitText: "Submit Documents",
      canSubmit: true,
      className: "border-sky-200 bg-sky-50",
    };
  }

  return {
    status: "Available",
    title: "Documents required",
    message: "Upload one document for each requirement. Uploaded documents are reviewed by the training team.",
    nextAction: "Choose the required documents and submit them for review.",
    submitText: "Submit Documents",
    canSubmit: true,
    className: "border-slate-200 bg-slate-50",
  };
}

function getDocumentMessage(status) {
  const normalized = normalizeWorkflowStatus(status);
  if (normalized === "pending") return "This document is waiting for admin review.";
  if (normalized === "approved") return "This document has been approved.";
  return "";
}

function normalizeWorkflowStatus(status) {
  const value = String(status || "").replace(/\s+/g, "").toLowerCase();
  if (value.includes("readytosubmit")) return "ready";
  if (value.includes("returned") || value.includes("rejected") || value.includes("expired")) return "returned";
  if (value.includes("approved")) return "approved";
  if (value.includes("pendingreview") || value.includes("uploaded") || value.includes("submitted")) return "pending";
  return "required";
}

function DocumentWorkflowTimeline({ items }) {
  if (!items.some((item) => item.fileName || item.status !== "Required")) {
    return <div className="text-sm text-slate-500">No document workflow is available yet.</div>;
  }

  return (
    <ol className="space-y-4 border-l-2 border-slate-200 pl-5">
      {items.map((item) => {
        const current = item.status !== "Required";
        return (
          <li key={item.title} className="relative">
            <span className={`absolute -left-[1.72rem] top-1 h-3 w-3 rounded-full ${current ? "bg-orange-500 ring-4 ring-orange-100" : "bg-slate-300"}`} />
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-[#0A2540]">{item.title}: {item.heading}</span>
              <StatusBadge status={item.status} />
              {item.updatedDate && <span className="text-xs text-slate-500">{new Date(item.updatedDate).toLocaleString("en-AU")}</span>}
            </div>
            {item.fileName && <div className="mt-1 text-sm text-slate-600">File: {item.fileName}</div>}
            {item.remarks && <div className="mt-1 text-sm text-slate-600">Remarks: {item.remarks}</div>}
            {item.updatedBy && <div className="mt-1 text-xs text-slate-500">Done by {item.updatedBy}</div>}
          </li>
        );
      })}
    </ol>
  );
}

function optionsHaveUsableDocument(options, uploadedByType, selection) {
  if (selection?.file) return true;
  return options.some(([id]) => {
    const document = uploadedByType.get(id);
    if (!getDocumentValue(document, "studentDocId")) return false;
    return normalizeWorkflowStatus(getDocumentValue(document, "workFlowStatusName")) !== "returned";
  });
}

function getDocumentValue(document, key) {
  if (!document) return undefined;
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  return document[key] ?? document[pascalKey];
}

function getWorkflowButtons(document) {
  const rawButtons = getDocumentValue(document, "buttonList");
  if (!Array.isArray(rawButtons)) return [];
  return rawButtons.filter((button) => !button.hideButton && !button.HideButton);
}

function StatusBadge({ status }) {
  const lower = String(status).toLowerCase();
  const className = lower.includes("approved")
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : lower.includes("returned") || lower.includes("rejected")
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : lower.includes("review") || lower.includes("uploaded")
        ? "border-amber-200 bg-amber-50 text-amber-700"
        : "border-slate-200 bg-white text-slate-600";
  return <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${className}`}>{status}</span>;
}

function getActionClass(text) {
  const lower = String(text).toLowerCase();
  if (lower.includes("approve")) return "wc-btn-primary h-10 bg-emerald-600 px-3 text-xs hover:bg-emerald-700";
  if (lower.includes("return") || lower.includes("reject")) return "inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 text-xs font-bold text-rose-700 transition-colors hover:bg-rose-50";
  return "wc-btn-secondary h-10 px-3 text-xs";
}

function ReturnReasonDialog({ value, onChange, onClose, onConfirm, processing }) {
  const open = Boolean(value);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Return document</DialogTitle>
          <DialogDescription>Enter the return reason for the student.</DialogDescription>
        </DialogHeader>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-500" htmlFor="document-return-reason">
            Return reason
          </label>
          <textarea
            id="document-return-reason"
            autoFocus
            value={value?.remarks || ""}
            onChange={(event) => onChange(event.target.value)}
            className="wc-input min-h-[120px]"
            placeholder="Enter the return reason for this document"
          />
        </div>
        <DialogFooter>
          <button type="button" className="wc-btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="wc-btn-primary" onClick={onConfirm} disabled={processing || !value?.remarks?.trim()}>
            {processing ? "Saving..." : "Confirm Return"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
